import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { Heart, Bone, Brain, Baby, Scissors, Activity, ArrowRight, Clock, DollarSign, Stethoscope, Leaf } from "lucide-react";

const treatments = [
  {
    id: "cardiology",
    icon: Heart,
    title: "Cardiology",
    overview: "India is a global leader in cardiac care, offering advanced treatments including minimally invasive heart surgery, bypass, valve replacement, and angioplasty at a fraction of international costs.",
    procedures: ["Coronary Artery Bypass Grafting (CABG)", "Heart Valve Replacement", "Angioplasty & Stenting", "Pacemaker Implantation", "Heart Transplant", "Minimally Invasive Cardiac Surgery"],
    costRange: "$3,000 - $7,000",
    costCompare: "70-90% less than US/UK",
    recovery: "5-14 days in hospital, 4-8 weeks full recovery",
    color: "bg-red-50 text-red-600 border-red-100",
  },
  {
    id: "orthopedics",
    icon: Bone,
    title: "Orthopedics",
    overview: "Advanced orthopedic care including robotic-assisted joint replacements, spine surgery, and sports medicine treatments using the latest technology and techniques.",
    procedures: ["Total Knee Replacement", "Total Hip Replacement", "Spine Surgery (Disc, Fusion)", "Arthroscopy", "ACL Reconstruction", "Shoulder Replacement"],
    costRange: "$4,000 - $8,000",
    costCompare: "75-85% less than US/UK",
    recovery: "3-7 days in hospital, 6-12 weeks full recovery",
    color: "bg-blue-50 text-blue-600 border-blue-100",
  },
  {
    id: "oncology",
    icon: Activity,
    title: "Oncology",
    overview: "Comprehensive cancer treatment programs including surgery, chemotherapy, immunotherapy, and radiation therapy at internationally accredited cancer centers.",
    procedures: ["Surgical Oncology", "Chemotherapy Programs", "Radiation Therapy (IMRT, CyberKnife)", "Immunotherapy", "Bone Marrow Transplant", "Targeted Therapy"],
    costRange: "$5,000 - $25,000",
    costCompare: "60-80% less than US/UK",
    recovery: "Varies by treatment type and stage",
    color: "bg-purple-50 text-purple-600 border-purple-100",
  },
  {
    id: "neurology",
    icon: Brain,
    title: "Neurology",
    overview: "World-class neurological and neurosurgical care for conditions ranging from brain tumors to movement disorders, utilizing cutting-edge technology.",
    procedures: ["Brain Tumor Surgery", "Deep Brain Stimulation", "Epilepsy Surgery", "Spine Neurosurgery", "Stroke Treatment", "Neuro Rehabilitation"],
    costRange: "$5,000 - $15,000",
    costCompare: "70-85% less than US/UK",
    recovery: "7-21 days in hospital, varies by procedure",
    color: "bg-amber-50 text-amber-600 border-amber-100",
  },
  {
    id: "fertility",
    icon: Baby,
    title: "Fertility",
    overview: "India offers advanced fertility treatments with high success rates at significantly lower costs. Our partner clinics use the latest reproductive technologies.",
    procedures: ["In Vitro Fertilization (IVF)", "Intrauterine Insemination (IUI)", "ICSI Treatment", "Egg Freezing", "Surrogacy Programs", "Fertility Assessment"],
    costRange: "$2,500 - $5,000",
    costCompare: "65-80% less than US/UK",
    recovery: "Minimal downtime, 2-3 weeks per cycle",
    color: "bg-pink-50 text-pink-600 border-pink-100",
  },
  {
    id: "cosmetic",
    icon: Scissors,
    title: "Cosmetic Surgery",
    overview: "Board-certified plastic surgeons offering a full range of cosmetic and reconstructive procedures in state-of-the-art facilities.",
    procedures: ["Rhinoplasty", "Liposuction", "Breast Augmentation/Reduction", "Facelift", "Hair Transplant", "Tummy Tuck"],
    costRange: "$1,500 - $6,000",
    costCompare: "70-85% less than US/UK",
    recovery: "1-3 days in hospital, 2-6 weeks full recovery",
    color: "bg-teal-50 text-teal-600 border-teal-100",
  },
  {
    id: "rehabilitation",
    icon: Activity,
    title: "Rehabilitation",
    overview: "Comprehensive rehabilitation services combining modern physiotherapy with traditional Ayurvedic recovery methods. Expert teams help patients regain mobility, strength, and independence after surgery, injury, or neurological events.",
    procedures: ["Post-Surgical Physiotherapy", "Neurological Rehabilitation (Stroke, Spinal)", "Cardiac Rehabilitation", "Orthopedic Rehabilitation", "Sports Injury Recovery", "Geriatric Rehabilitation", "Speech & Swallowing Therapy", "Occupational Therapy"],
    costRange: "$800 - $4,000",
    costCompare: "70-85% less than US/UK",
    recovery: "2-12 week supervised programs",
    color: "bg-amber-50 text-amber-700 border-amber-100",
  },
  {
    id: "ayurveda",
    icon: Leaf,
    title: "Ayurveda & Wellness",
    overview: "Kerala is the birthplace of Ayurveda, offering authentic traditional treatments that have been practiced for over 5,000 years. Our partner institutions in Kerala provide world-renowned Panchakarma therapies and holistic wellness programs.",
    procedures: ["Panchakarma Detox", "Abhyanga (Oil Massage)", "Shirodhara", "Pizhichil (Oil Bath)", "Njavarakizhi (Rice Bolus)", "Rasayana (Rejuvenation)", "Yoga & Meditation Programs", "Stress Management Programs"],
    costRange: "$500 - $3,000",
    costCompare: "Authentic treatments only available in Kerala, India",
    recovery: "14-28 day treatment programs, immediate wellness benefits",
    color: "bg-green-50 text-green-700 border-green-100",
  },
];

export default function TreatmentsPage() {
  const { t } = useLanguage();
  return (
    <div data-testid="treatments-page">
      <SEO title="Treatments" description="Specialized medical treatments in Kerala, India - Cardiology, Orthopedics, Oncology, Neurology, Fertility, Cosmetic Surgery, and Ayurveda. Save 60-90% on treatment costs." path="/treatments" keywords="medical treatment India, cardiology India, orthopedics India, Ayurveda Kerala, cancer treatment India, fertility treatment India" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <span className="overline">{t("treat.overline")}</span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
              {t("treat.title")} <span className="font-medium text-[#00A4A9]">{t("treat.titleHighlight")}</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-3xl mb-12">
              {t("treat.desc")}
            </p>
          </FadeIn>

          <Tabs defaultValue="cardiology" className="w-full">
            <FadeIn delay={100}>
              <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto mb-10" data-testid="treatment-tabs">
                {treatments.map((tr) => (
                  <TabsTrigger
                    key={tr.id}
                    value={tr.id}
                    data-testid={`tab-${tr.id}`}
                    className="rounded-full px-5 py-2.5 text-sm data-[state=active]:bg-[#00A4A9] data-[state=active]:text-white border border-slate-200 data-[state=active]:border-[#00A4A9]"
                  >
                    <tr.icon className="w-4 h-4 mr-2" />
                    {(() => { const v = t(`treat.${tr.id}.t`); return (typeof v === "string" && v.startsWith("treat.")) ? tr.title : v; })()}
                  </TabsTrigger>
                ))}
              </TabsList>
            </FadeIn>

            {treatments.map((tr) => (
              <TabsContent key={tr.id} value={tr.id} data-testid={`treatment-content-${tr.id}`}>
                <FadeIn>
                  <div className="bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`w-12 h-12 rounded-xl ${tr.color} flex items-center justify-center`}>
                        <tr.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h2 className="font-outfit text-2xl md:text-3xl font-medium text-[#03045e]">{t(`treat.${tr.id}.t`) || tr.title}</h2>
                      </div>
                    </div>
                    <p className="text-slate-600 leading-relaxed mb-8">{t(`treat.${tr.id}.o`) || tr.overview}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      <div className="bg-[#e0f2fe] rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-5 h-5 text-[#00A4A9]" />
                          <span className="font-outfit font-medium text-[#03045e]">{t("treat.costRange")}</span>
                        </div>
                        <div className="text-xl font-outfit font-semibold text-[#00A4A9]">{tr.costRange}</div>
                        <div className="text-xs text-slate-500 mt-1">{tr.costCompare}</div>
                      </div>
                      <div className="bg-[#F1F5F9] rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-5 h-5 text-[#03045e]" />
                          <span className="font-outfit font-medium text-[#03045e]">{t("treat.recoveryTime")}</span>
                        </div>
                        <div className="text-sm text-slate-600">{tr.recovery}</div>
                      </div>
                      <div className="bg-[#F9F9F8] rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <Stethoscope className="w-5 h-5 text-[#005f8a]" />
                          <span className="font-outfit font-medium text-[#03045e]">{t("treat.procedures")}</span>
                        </div>
                        <div className="text-sm text-slate-600">{tr.procedures.length} {t("treat.availProc").toLowerCase()}</div>
                      </div>
                    </div>

                    <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">{t("treat.availProc")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                      {tr.procedures.map((p) => (
                        <div key={p} className="flex items-center gap-2 text-sm text-slate-600">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00A4A9]" />
                          {p}
                        </div>
                      ))}
                    </div>

                    <Link to="/contact" data-testid={`treatment-inquiry-${tr.id}`}>
                      <Button className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white btn-hover">
                        {t("treat.inquireAbout")} {t(`treat.${tr.id}.t`) || tr.title} <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </FadeIn>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="section-padding bg-white" data-testid="cost-comparison-section">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <span className="overline">{t("treat.costOverline")}</span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-medium text-[#03045e] mt-3 tracking-tight">
                {t("treat.costTitle")}
              </h2>
            </div>
          </FadeIn>
          <FadeIn delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="cost-comparison-table">
                <thead>
                  <tr className="bg-[#03045e] text-white">
                    <th className="text-left p-4 rounded-tl-xl font-outfit font-medium">{t("treat.procedure")}</th>
                    <th className="text-center p-4 font-outfit font-medium">{t("treat.india")}</th>
                    <th className="text-center p-4 font-outfit font-medium">{t("treat.usa")}</th>
                    <th className="text-center p-4 font-outfit font-medium">{t("treat.uk")}</th>
                    <th className="text-center p-4 rounded-tr-xl font-outfit font-medium">{t("treat.savings")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Heart Bypass", "$5,000", "$75,000", "$40,000", "Up to 93%"],
                    ["Hip Replacement", "$6,000", "$50,000", "$30,000", "Up to 88%"],
                    ["Knee Replacement", "$5,500", "$45,000", "$25,000", "Up to 88%"],
                    ["IVF Treatment", "$3,000", "$15,000", "$10,000", "Up to 80%"],
                    ["Dental Implants", "$800", "$4,000", "$3,000", "Up to 80%"],
                    ["Rhinoplasty", "$2,500", "$8,000", "$6,000", "Up to 69%"],
                  ].map(([proc, india, usa, uk, save], i) => (
                    <tr key={proc} className={i % 2 === 0 ? "bg-[#F9F9F8]" : "bg-white"}>
                      <td className="p-4 text-sm font-medium text-[#03045e]">{proc}</td>
                      <td className="p-4 text-center text-sm font-semibold text-[#00A4A9]">{india}</td>
                      <td className="p-4 text-center text-sm text-slate-500">{usa}</td>
                      <td className="p-4 text-center text-sm text-slate-500">{uk}</td>
                      <td className="p-4 text-center">
                        <Badge className="bg-[#e0f2fe] text-[#00A4A9] hover:bg-[#e0f2fe] border-0">{save}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
